import React from "react";
import {
  Button,
  ButtonContainer,
  SectionHeading,
  ContentSection,
  Spinner,
} from "@vwfs-bronson/bronson-react";
import { Formik } from "formik";
import { Output } from "../Text";
import PropTypes from "prop-types";
import { FormSectionGroup2 } from "../pages/Sections/FormSection";
import { wait } from "../common/wait";
import { Analytics } from "../services/analytics";
import { setActualPage } from "../pages/page.utils";

class DataForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      section: props.initialSection | 0,
      values: props.initialValues,
    };

    // Track the first section view change, others are handled in handleSubmit()
    if (this.state.section === 0) {
      this.trackSectionChange();
    }

    this.previousState = {};
    this.pos = {};
    this.bic = "";
  }

  pageName = "Application form";

  defaultTrackingData = {
    [Analytics.keys.pageName()]: this.pageName,
    [Analytics.keys.formType()]: "Personal data",
  };

  defaultSpecificData = {};

  componentDidMount() {
    setActualPage(this.pageName);
    if (this.state.section === 0) {
      window.scrollTo(0, 0);
    } else {
      this.scrollOnRedirect(this.state.section);
    }
  }

  next = (values) =>
    this.setState((state) => ({
      section: Math.min(state.section + 1, this.props.children.length - 1),
      values,
    }));

  previous = () =>
    this.setState((state) => ({
      section: Math.max(state.section - 1, 0),
    }));

  getSectionYOffset = (section) => {
    const pos = this.pos[section].getBoundingClientRect().top;
    return pos ? pos : 0;
  };

  scrollOnRedirect = async (section) => {
    await wait(1000);
    this.scrollSectionIntoView(section);
  };

  scrollSectionIntoView = (section) => {
    window.scrollBy({
      top: this.getSectionYOffset(section),
      left: 0,
      behavior: "smooth",
    });
  };

  handleSubmit = (values, bag) => {
    const { children, onSubmit } = this.props;
    const { section } = this.state;
    const isLastPage = section === React.Children.count(children) - 1;

    const component = React.Children.toArray(children)[section];
    if (this.props.onSectionSubmit) {
      this.props.onSectionSubmit({ values });
    }
    const onChildSectionSubmit = component.props.onSectionSubmit;
    if (onChildSectionSubmit) {
      onChildSectionSubmit(values);
    }

    if (isLastPage) {
      this.trackSubmitForm();
      return onSubmit(values, bag);
    } else {
      this.trackSectionChange();
      bag.setTouched({});
      bag.setSubmitting(false);
      this.next(values);
      this.scrollSectionIntoView(section);
    }
  };

  getNextSectionName = (index) => {
    const sectionName =
      [
        "Personal data", // 0
        "Contact data", // 1
        "Employment data", // 2
        "Financial data", // 3
        "Bank account data", // 4
        "Document upload", // 5 (LCV only)
        "Summary", // 6 (LCV only)
      ][index] || "";

    return sectionName;
  };

  getButtonTrackInfo = (index) => {
    if (index === React.Children.count(this.props.children) - 1) {
      // last element, submitting page, not changing sections
      return "";
    }

    const eventType =
      [
        "ProductInformation:Personal Information", // 0
        "ProductInformation:Contact Information", // 1
        "ProductInformation:Employment Information", // 2
        "ProductInformation:Financial Information", // 3
        "interAction", // 4
      ][index] || "";

    const eventAction =
      [
        "Provided", // 0
        "Provided", // 1
        "Provided", // 2
        "Provided", // 3
        "Success", // 4
      ][index] || "";

    const buttonTrackInfo = {
      [Analytics.keys.eventType(0)]: eventType,
      [Analytics.keys.eventAction(0)]: eventAction,
    };

    return buttonTrackInfo;
  };

  trackSectionChange() {
    const sectionName = this.getNextSectionName(this.state.section);

    const specificData = {
      [Analytics.keys.viewChange()]: sectionName,
      [Analytics.keys.formName()]: sectionName,
    };
    this.defaultSpecificData = { ...this.defaultTrackingData, ...specificData };
  }

  trackButtonWeiter = () => {
    const trackingData = this.getButtonTrackInfo(this.state.section);
    Analytics.setTrackingData({ ...this.defaultSpecificData, ...trackingData });
    Analytics.trackInteraction();
  };

  trackSubmitForm = () => {
    const submitData = {
      [Analytics.keys.eventType(0)]: "PaymentData",
      [Analytics.keys.eventAction(0)]: "Provided",
      [Analytics.keys.eventType(1)]: "RequiredContractData",
      [Analytics.keys.eventAction(1)]: "Provided",
      [Analytics.keys.linkInformation(0)]: "Required contract provided",
    };
    Analytics.setTrackingData({ ...this.defaultSpecificData, ...submitData });
    Analytics.trackInteraction();
  };

  render() {
    const { children } = this.props;
    const { section, values } = this.state;
    const isLastSection = section === React.Children.count(children) - 1;

    return (
      <ContentSection pageWrap>
        <SectionHeading level={"1"} smallSpacing={true} primary>
          {Output.resolve("pages.applicationForm.title1")}
        </SectionHeading>

        <p>{Output.resolve("pages.applicationForm.title3")}</p>
        <FormSectionGroup2>
          <Formik
            initialValues={values}
            validate={async (values) => {
              const component = React.Children.toArray(children)[section];
              let validation = {};
              if (component?.props?.validate) {
                validation = await component.props.validate(values, this.previousState);
              }
              this.previousState = {
                values: { ...values },
                errors: validation
              }
              return validation;
            }}
            validateOnChange
            validateOnBlur
            enableReinitialize={false}
            onSubmit={this.handleSubmit}
          >
            {formProps => {
              const {
                values,
                handleSubmit,
                isSubmitting,
                isValidating,
                setFieldValue,
              } = formProps;
              return (
                <>
                  {React.Children.map(children, (child, index) => {
                    return React.cloneElement(child, {
                      containerRef: (e) => {
                        this.pos[index] = e;
                      },
                      form: { onSubmit: handleSubmit, setFieldValue },
                      values,
                      closed: section !== index,
                      success: index < section,
                      showEditButton: index < section,
                      onIconClick: () => {
                        if (index < section) {
                          this.setState({
                            section: index,
                          });
                        }
                      },
                      buttons: (
                        <ButtonContainer right>
                          {!isLastSection ? (
                            <Button
                              className={"appFixedButton"}
                              type={"submit"}
                              small
                              onClick={() => this.trackButtonWeiter()}
                              disabled={this.props.isLoading || isValidating || isSubmitting}
                            >
                              {this.props.isLoading || isSubmitting ? (
                                <Spinner small />
                              ) : (
                                <>
                                  {Output.resolve(
                                    "pages.applicationForm.buttonNext"
                                  )}
                                </>
                              )}
                            </Button>
                          ) : (
                            <Button
                              className={"appFixedButton"}
                              type={"submit"}
                              small
                              disabled={isSubmitting}
                            >
                              {Output.resolve(
                                "pages.applicationForm.buttonSummary"
                              )}
                            </Button>
                          )}
                        </ButtonContainer>
                      ),
                    });
                  })}
                </>
              );
            }}
          </Formik>
        </FormSectionGroup2>
      </ContentSection>
    );
  }
}

DataForm.propTypes = {
  initialValues: PropTypes.object,
  initialSection: PropTypes.number,
  onSectionSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
};

export { DataForm };
